'use client';

import { useState } from 'react';
import { Send, Bot, User, Sparkles } from 'lucide-react';

export default function DSLMConsole() {
    const [messages, setMessages] = useState<{role: 'user'|'assistant', text: string}[]>([
        { role: 'assistant', text: '안녕하세요! 저는 디자인인사이트의 자체 도메인 특화 모델 (DSLM)입니다. 모델 추론은 강력하고 가벼운 Gemma 4 (MLX 가속) 기반으로 동작합니다. 무엇을 도와드릴까요?' }
    ]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!input.trim() || isLoading) return;

        const userMsg = input.trim();
        setInput('');
        setMessages(prev => [...prev, { role: 'user', text: userMsg }]);
        setIsLoading(true);

        try {
            const res = await fetch('/api/dslm', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ query: userMsg }),
            });

            if (!res.ok) throw new Error('Network error');

            const data = await res.json();
            
            setMessages(prev => [...prev, { 
                role: 'assistant', 
                text: data.answer || '응답을 생성할 수 없습니다.'
            }]);
        } catch (error) {
            setMessages(prev => [...prev, { 
                role: 'assistant', 
                text: '죄송합니다. 현재 본부의 MLX 서버 오프라인 상태이거나 네트워크 장애가 발생했습니다.'
            }]);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="w-full max-w-4xl mx-auto my-16 p-6 rounded-2xl border border-white/10 bg-white/[0.02] shadow-2xl backdrop-blur-xl">
            <div className="flex items-center gap-3 mb-6 border-b border-white/5 pb-4">
                <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                    <h3 className="text-xl font-bold text-white">DSLM Live Console</h3>
                    <p className="text-xs text-gray-400">Powered by On-Premise Gemma 4 (MLX)</p>
                </div>
            </div>

            <div className="h-[400px] overflow-y-auto pr-4 mb-4 space-y-4 flex flex-col">
                {messages.map((msg, i) => (
                    <div key={i} className={`flex gap-3 max-w-[85%] ${msg.role === 'user' ? 'ml-auto flex-row-reverse' : ''}`}>
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${msg.role === 'user' ? 'bg-blue-600' : 'bg-purple-600'}`}>
                            {msg.role === 'user' ? <User className="w-4 h-4 text-white" /> : <Bot className="w-4 h-4 text-white" />}
                        </div>
                        <div className={`p-4 rounded-2xl text-sm leading-relaxed ${msg.role === 'user' ? 'bg-blue-600 text-white rounded-tr-none' : 'bg-gray-800 border border-white/5 text-gray-200 rounded-tl-none'}`}>
                            {msg.text}
                        </div>
                    </div>
                ))}
                {isLoading && (
                    <div className="flex gap-3 max-w-[85%]">
                        <div className="w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center shrink-0">
                            <Bot className="w-4 h-4 text-white animate-pulse" />
                        </div>
                        <div className="p-4 rounded-2xl text-sm leading-relaxed bg-gray-800 border border-white/5 text-gray-200 rounded-tl-none flex items-center gap-2">
                            <span className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                            <span className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                            <span className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                        </div>
                    </div>
                )}
            </div>

            <form onSubmit={handleSubmit} className="relative">
                <input 
                    type="text" 
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    placeholder="DSLM에게 도메인 지식이나 업무 관련 질문을 해보세요..."
                    className="w-full bg-gray-900 border border-white/10 rounded-xl px-4 py-4 pr-14 text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/50 transition-all"
                />
                <button 
                    type="submit" 
                    disabled={isLoading || !input.trim()}
                    className="absolute right-2 top-2 bottom-2 aspect-square rounded-lg bg-purple-600 hover:bg-purple-500 disabled:bg-gray-700 disabled:opacity-50 flex items-center justify-center transition-colors"
                >
                    <Send className="w-4 h-4 text-white" />
                </button>
            </form>
        </div>
    );
}
